import { useCallback, useRef, useState } from 'react';

import { useI18n } from '@change-corgi/core-react/i18n';
import { useTracking } from '@change-corgi/core-react/tracking';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { GOAL_PRESETS } from '../../constants';
import { saveShareGoal } from '../useSetGoalStep/api';

type UseSetGoalChipsOptions = {
	petitionId: string;
	userId: string | undefined;
};

function isGoalPreset(value: number): value is (typeof GOAL_PRESETS)[number] {
	return (GOAL_PRESETS as readonly number[]).includes(value);
}

/**
 * Discrete per-preset copy (not threshold-based like the slider's getGoalMotivation) since chips
 * only ever land on exactly one of GOAL_PRESETS. A switch over the literal union (no `default`)
 * makes a preset-list change a compile error here instead of a silently missing motivation, same
 * guarantee the previous `Record` lookup gave -- but only translates the one preset that's active.
 */
function getChipMotivation(
	goalValue: (typeof GOAL_PRESETS)[number],
	translate: ReturnType<typeof useI18n>['translate'],
): { emoji: string; message: string } {
	switch (goalValue) {
		case 5:
			return { emoji: '⭐', message: translate('corgi.psf.share.sharing_goal.chips.motivation_5') };
		case 10:
			return { emoji: '⭐', message: translate('corgi.psf.share.sharing_goal.chips.motivation_10') };
		case 15:
			return { emoji: '💪', message: translate('corgi.psf.share.sharing_goal.chips.motivation_15') };
		case 20:
			return { emoji: '🚀', message: translate('corgi.psf.share.sharing_goal.chips.motivation_20') };
		default: {
			const exhaustive: never = goalValue;
			return exhaustive;
		}
	}
}

/**
 * Sibling to useSetGoalStep for the variant_1 arm. Diverges deliberately: goalValue starts
 * unset (no localStorage/initialShareGoal seeding -- "nothing preselected" per R3) and saveGoal
 * no-ops when nothing was ever tapped, instead of falling back to DEFAULT_GOAL. Kept separate so
 * useSetGoalStep (control/fake_control) stays byte-for-byte untouched.
 */
export function useSetGoalChips({ petitionId, userId }: UseSetGoalChipsOptions) {
	const { translate } = useI18n();
	const utilityContext = useUtilityContext();
	const { errorReporter } = utilityContext;
	const track = useTracking();

	const [goalValue, setGoalValueState] = useState<number | undefined>(undefined);
	const [prevPetitionId, setPrevPetitionId] = useState(petitionId);

	// Render-phase state sync, mirroring useSetGoalStep: reset selection on a petition switch
	// (same instance, new petitionId) rather than saving a chip tapped for a different petition.
	if (petitionId !== prevPetitionId) {
		setPrevPetitionId(petitionId);
		setGoalValueState(undefined);
	}

	// Tapping a new chip selects it and fires the event; tapping the active chip again (or any
	// other deselect path) clears it silently -- no event for the "back to none" transition.
	const selectGoal = useCallback(
		(value: number | undefined) => {
			setGoalValueState(value);
			if (value != null) {
				void track('psf_share_goal_chip_select', { goal_value: value, petition_id: petitionId });
			}
		},
		[track, petitionId],
	);

	const lastSavedRef = useRef<{ petitionId: string; goalValue: number } | null>(null);

	const saveGoal = useCallback(async () => {
		if (!userId) return;
		if (goalValue == null) return;
		if (lastSavedRef.current?.petitionId === petitionId && lastSavedRef.current.goalValue === goalValue) return;
		lastSavedRef.current = { petitionId, goalValue };
		try {
			await saveShareGoal({ petitionId, goalValue }, utilityContext);
		} catch (error) {
			lastSavedRef.current = null;
			void errorReporter.report({
				error: new Error('Failed to save sharing goal to server', { cause: error }),
				params: { petitionId, goalValue },
			});
		}
	}, [goalValue, petitionId, userId, utilityContext, errorReporter]);

	const motivation = goalValue != null && isGoalPreset(goalValue) ? getChipMotivation(goalValue, translate) : undefined;

	return { goalValue, selectGoal, saveGoal, emoji: motivation?.emoji, message: motivation?.message };
}
